import React, {RefObject} from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";
import {
    KatBox,
    KatSelectionListItem,
    KatSelectionList,
    KatIcon,
    KatLabel,
    KatBadge
} from "@amzn/katal-react";
import RequestStatus from "src/model/RequestStatus";
import {GetProgramFeesResponse, ProgramFeeResultWithStatus, ProgramParam} from "src/model/ProgramFees"
import {ProgramMetadata, GetProgramMetadataResponse} from "src/model/ProgramMetadata"
import MetricsUtil from "src/utils/MetricsUtil";
import ApplicationState from "src/model/ApplicationState";
import ErrorStatusComponent from "../common/ErrorStatusComponent";
import {
    getCurrencyUnitConfig,
    SELECT_YOUR_PROGRAM,
    PROGRAM_NAME_OF_MFN,
    RECOMMENDATION_FBA,
    RECOMMENDATION_LOWASP,
    isLoginMode, hasRecommendationRank, getRecommendationRank, getProgramInventoryCountryListConfig, programWithInventoryAndStoreParam
} from "src/config/Config";
import {setAdditionalProgramFeesQueryStart} from "../../actions/ProgramFeesQueryAction";
import {setAdditionalNewProgramFeesQueryStart} from "../../actions/NewProgramFeesQueryAction";
import CurrencyItem from "src/model/CurrencyItem";
import ProgramProfitComponent from "./ProgramProfitComponent";
import ProgramCostComponent from "./ProgramCostComponent";
import {QueryProductInfo, UpdateQueryProductResponse} from "src/model/QueryProduct";
import LoadingStatusComponent from "../common/LoadingStatusComponent";
import ProgramRevenueComponent from "./ProgramRevenueComponent";
import ProgramInfoComponent from "src/components/program/ProgramInfoComponent";
import GetRecommendationResponse, {RecommendationInfo, RecommendationResponse} from "src/model/Recommendation";
import ProgramRecommendationComponent from "src/components/recommendation/ProgramRecommendationComponent";
import FormatUtil from "src/utils/FormatUtil";

interface StateProps {
    programMetadataRes: GetProgramMetadataResponse,
    recommendationRes: GetRecommendationResponse,
    programFeesRes: GetProgramFeesResponse,
    queryProductRes: UpdateQueryProductResponse,
}

interface OwnProps {
    readonly initialProgram: string;
    readonly metricsUtil?: MetricsUtil
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    programMetadataRes: state.programMetadataRes,
    recommendationRes: state.recommendationRes,
    programFeesRes: state.programFeesRes,
    queryProductRes: state.queryProductRes,
});

interface DispatchProps {
    readonly dispatchGetAdditionalProgramFees: (countryCode: string, queryProduct: QueryProductInfo, programIdList: Array<string>, programParamMap?: Record<string, ProgramParam>) => void,
    readonly dispatchGetAdditionalNewProgramFees: (countryCode: string, queryProduct: QueryProductInfo, programIdList: Array<string>, programParamMap?: Record<string, ProgramParam>) => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchGetAdditionalProgramFees: setAdditionalProgramFeesQueryStart,
        dispatchGetAdditionalNewProgramFees: setAdditionalNewProgramFeesQueryStart
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    salesPrice: CurrencyItem;
    shippingCharge: CurrencyItem;
    costPerUnit: CurrencyItem;
    selectedProgram: string;
    showProgramRecommendation: boolean;
}

export class ProgramComponent extends React.Component<Props, State> {
    private readonly metricsUtil: MetricsUtil;
    private readonly costComponentFirstInputRef: RefObject<any>;
    private readonly profitComponentFirstInputRef: RefObject<any>;

    constructor(props: Props) {
        super(props);

        const defaultCurrencyItem: CurrencyItem = {
            amount: 0, currency: getCurrencyUnitConfig(this.props.queryProductRes.countryCode)
        }

        this.state = {
            salesPrice: {amount: this.getDefaultSalesPrice(), currency: defaultCurrencyItem.currency},
            shippingCharge: {amount: this.getDefaultShippingCharge(), currency: defaultCurrencyItem.currency},
            costPerUnit: defaultCurrencyItem,
            selectedProgram: this.props.initialProgram,
            showProgramRecommendation: true,
        };

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        // Used for input box focus switch by Enter key, link input box cross inner components
        this.costComponentFirstInputRef = React.createRef<any>();
        this.profitComponentFirstInputRef = React.createRef<any>();

        this.handleChangeSalesPrice = this.handleChangeSalesPrice.bind(this);
        this.handleChangeShippingCharge = this.handleChangeShippingCharge.bind(this);
        this.handleChangeCostPerUnit = this.handleChangeCostPerUnit.bind(this);
        this.handleChangeProgramCard = this.handleChangeProgramCard.bind(this);
        this.handleDismissProgramRecommendation = this.handleDismissProgramRecommendation.bind(this);
    }


    private isProgramMetadataValid = () => {
        if (this.props.programMetadataRes.requestStatus === RequestStatus.SUCCESS
            && this.props.programMetadataRes.data != null
            && this.props.programMetadataRes.data.succeed
            && this.props.programMetadataRes.data.programInfoList != null) {
            return true;
        }
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("program.programMetadata", "error");
        }
        return false;
    }

    private isProgramFeesDataValid = (selectedProgram: string) => {
        // return ture when SELECT_YOUR_PROGRAM is selected
        if (selectedProgram === SELECT_YOUR_PROGRAM) {
            return true;
        }
        if (this.props.programFeesRes.requestStatus != RequestStatus.SUCCESS
            || this.props.programFeesRes.programFeesStore == null
            || this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap == null) {
            if (this.metricsUtil) {
                this.metricsUtil.publishCounter("program.programFeesData", "error");
            }
            return false;
        }
        const programFeeResultWithStatus: ProgramFeeResultWithStatus = this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap[selectedProgram];
        if (programFeeResultWithStatus == null
            || programFeeResultWithStatus.requestStatus == RequestStatus.ERROR
            || (programFeeResultWithStatus.requestStatus == RequestStatus.SUCCESS
                && programFeeResultWithStatus.programFeeResult == null)) {
            if (this.metricsUtil) {
                this.metricsUtil.publishCounter("program.programFeeData", "error");
            }
            return false;
        }
        return true;
    }

    private isLoading = (selectedProgram: string) => {
        // return false when SELECT_YOUR_PROGRAM is selected
        if (selectedProgram === SELECT_YOUR_PROGRAM) {
            return false;
        }

        var isLoading: boolean = false;
        if (this.props.programFeesRes.programFeesStore != null) {
            const programFeeResultWithStatus: ProgramFeeResultWithStatus = this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap[selectedProgram];
            if (programFeeResultWithStatus && programFeeResultWithStatus.requestStatus == RequestStatus.LOADING) {
                isLoading = true;
            }
        }
        return isLoading;
    }

    private isUpdating = (selectedProgram: string) => {
        // return false when SELECT_YOUR_PROGRAM is selected
        if (selectedProgram === SELECT_YOUR_PROGRAM) {
            return false;
        }

        var isUpdating: boolean = false;
        if (this.props.programFeesRes.programFeesStore != null) {
            const programFeeResultWithStatus: ProgramFeeResultWithStatus = this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap[selectedProgram];
            if (programFeeResultWithStatus && programFeeResultWithStatus.requestStatus == RequestStatus.UPDATING) {
                isUpdating = true;
            }
        }
        return isUpdating;
    }

    getDefaultSalesPrice(): number {
        let res = 0;
        if (this.props.queryProductRes.queryProduct.afnPriceStr != null) {
            res = +this.props.queryProductRes.queryProduct.afnPriceStr;
        } else if (this.props.queryProductRes.queryProduct.mfnPriceStr != null) {
            res = +this.props.queryProductRes.queryProduct.mfnPriceStr;
        }
        return res;
    }

    getDefaultShippingCharge(): number {
        let res = 0;
        if (this.props.queryProductRes.queryProduct.mfnShippingPriceStr != null) {
            res = +this.props.queryProductRes.queryProduct.mfnShippingPriceStr;
        }
        return res;
    }

    getProgramName(programName: string): string {
        let name = "";
        const {t, programMetadataRes} = this.props;
        if (programName == undefined || programName == null || programName == SELECT_YOUR_PROGRAM) {
            name = t("SC_FBA_Calc_dropdown_compare");
        } else {
            if (programMetadataRes != null && programMetadataRes.data != null && programMetadataRes.data.programInfoList != null) {
                const programMetadataArray: Array<ProgramMetadata> = programMetadataRes.data.programInfoList;
                programMetadataArray.forEach(programMetadata => {
                    if (programName == programMetadata.name) {
                        name = this.generateProgramName(programMetadata);
                    }
                })
            }
        }
        return name;
    }

    /**
     * get ProgramMetadata of the selectedProgram
     * @param selectedProgram
     * @returns programMetadata if exist, undefined otherwise
     */
    private getProgramMetadata = (selectedProgram: string): ProgramMetadata | undefined => {
        const {programMetadataRes} = this.props;
        if (programMetadataRes.data == null) {
            return undefined;
        }
        const programMetadataArray: Array<ProgramMetadata> = programMetadataRes.data.programInfoList;
        const programMetadataMap: Map<string, ProgramMetadata> = new Map<string, ProgramMetadata>(
            programMetadataArray.map(item => [item.name, item] as [string, ProgramMetadata])
        );
        return programMetadataMap.get(selectedProgram);
    }

    private getProgramCandidateForRecommendation(): string | undefined {
        const {queryProductRes, programMetadataRes} = this.props;
        if (!isLoginMode() || programMetadataRes.data == null) {
            return undefined;
        }
        const countryCode = queryProductRes.countryCode;
        const programMetadataArray: Array<ProgramMetadata> = programMetadataRes.data.programInfoList;
        const candidates = programMetadataArray.filter(p => hasRecommendationRank(countryCode, p.name))
            .sort((p1, p2) => {
                return getRecommendationRank(countryCode, p1.name) - getRecommendationRank(countryCode, p2.name);
            });
        if (candidates.length == 0) {
            return undefined;
        }
        return candidates[0].name;
    }

    private getProgramRecommendation(selectedProgram: string, candidateProgram?: string): RecommendationInfo | undefined {
        if (!isLoginMode() || candidateProgram == undefined || selectedProgram != candidateProgram
            || this.props.recommendationRes.requestStatus != RequestStatus.SUCCESS) {
            return undefined;
        }
        const recommendationRes: RecommendationResponse = this.props.recommendationRes.data;
        const countryCode = this.props.queryProductRes.countryCode;
        if (recommendationRes == null) {
            return undefined;
        }
        const recommendationInfos: Array<RecommendationInfo> = recommendationRes.recommendationInfos;
        if (recommendationInfos == undefined || recommendationRes.countryCode != countryCode) {
            return undefined;
        }
        return recommendationInfos.find(r => r.communicationType === RECOMMENDATION_FBA);
    }

    private isRecommendedProgram(selectedProgram: string): boolean {
        const candidate = this.getProgramCandidateForRecommendation();
        const programRecommendation = this.getProgramRecommendation(selectedProgram, candidate);
        return programRecommendation != undefined;
    }

    private getPriceRecommendation(): RecommendationInfo | undefined {
        if (!isLoginMode() || this.props.recommendationRes.requestStatus != RequestStatus.SUCCESS) {
            return undefined;
        }
        const recommendationRes: RecommendationResponse = this.props.recommendationRes.data;
        const countryCode = this.props.queryProductRes.countryCode;
        if (recommendationRes == null) {
            return undefined;
        }
        const recommendationInfos: Array<RecommendationInfo> = recommendationRes.recommendationInfos;
        if (recommendationInfos == undefined || recommendationRes.countryCode != countryCode) {
            return undefined;
        }
        return recommendationInfos.find(r => r.communicationType === RECOMMENDATION_LOWASP);
    }

    private generateProgramName = (programMetadata: ProgramMetadata): string => {
        const {t} = this.props;
        const stringArr: string[] = [];
        programMetadata.programStringIds.map((stringId, index) => (
            stringArr.push(t(stringId))
        ));
        return stringArr.join("-");
    }

    handleChangeCostPerUnit(costPerUnit: CurrencyItem) {
        this.setState({costPerUnit: costPerUnit});
    }

    handleChangeSalesPrice(salesPrice: CurrencyItem) {
        this.setState({salesPrice: salesPrice});
    }

    handleChangeShippingCharge(shippingCharge: CurrencyItem) {
        this.setState({shippingCharge: shippingCharge});
    }

    handleDismissProgramRecommendation() {
        this.setState({showProgramRecommendation: false});
    }

    /**
     * Things to do when changing the program selection
     * 1. change selectedProgram value in local state.
     * 2. if changed program is SELECT_YOUR_PROGRAM, do nothing futher.
     * 3. check if the coresponding program fee was queried. No action if yes, otherwise dispatch a call action
     */

    handleChangeProgramCard(event: KatSelectionList.ChangeEvent) {
        const changedProgram = event.detail.selected;
        this.setState({selectedProgram: changedProgram});

        if (changedProgram === SELECT_YOUR_PROGRAM) {
            // do nothing
        } else if (this.props.programFeesRes.programFeesStore == null
            || this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap[changedProgram] == null
            || this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap[changedProgram].requestStatus != RequestStatus.SUCCESS) {
            if (this.props.queryProductRes.queryProduct.isNewDefined) {
                this.props.dispatchGetAdditionalNewProgramFees(
                    this.props.queryProductRes.countryCode, this.props.queryProductRes.queryProduct, [changedProgram],
                    this.getDefaultProgramParam(changedProgram, this.props.queryProductRes.countryCode));
            } else {
                this.props.dispatchGetAdditionalProgramFees(
                    this.props.queryProductRes.countryCode, this.props.queryProductRes.queryProduct, [changedProgram],
                    this.getDefaultProgramParam(changedProgram, this.props.queryProductRes.countryCode));
            }
        }
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(changedProgram, "select");
            if (this.isRecommendedProgram(changedProgram)) {
                this.metricsUtil.publishCounter('programRecommendation:' + changedProgram, "select");
            }
        }
    }

    private getDefaultProgramParam(program: string, countryCode: string) {
        let programParamMap;
        if (programWithInventoryAndStoreParam.indexOf(program) >= 0) {
            const param = {
                marketplace: countryCode,
                inventoryPlace: getProgramInventoryCountryListConfig(program)[0],
                cee: true
            }
            programParamMap = {[program]: param};
        }
        return programParamMap;
    }

    private getBenefitString(recommendation: RecommendationInfo | undefined): string {
        if (recommendation == undefined) {
            return "";
        }
        const {t} = this.props;
        const benefit = FormatUtil.formatCurrency(getCurrencyUnitConfig(this.props.queryProductRes.countryCode),
            Number(recommendation.attributes['GMS_LIFT_PERCENT_30']));
        return t("SC_FBA_Calc_info_fbaRecommendationBenefit", {X: `${benefit}`});
    }

    private getSelectionListItem = () => {
        const {t, programMetadataRes} = this.props;
        if (programMetadataRes != null && programMetadataRes.data != null && programMetadataRes.data.programInfoList != null) {
            const programMetadataArray: Array<ProgramMetadata> = programMetadataRes.data.programInfoList;
            const candidate = this.getProgramCandidateForRecommendation();
            return (
                <KatSelectionList className="program-card-list" onChange={this.handleChangeProgramCard}>
                    {
                        programMetadataArray.map((programMetadata, index) => (
                            <KatSelectionListItem className="program-card-list-item" key={index} value={programMetadata.name}>
                                <ProgramInfoComponent program={programMetadata.name}
                                                      recommended={this.getProgramRecommendation(programMetadata.name, candidate) !== undefined}
                                                      recommendInfo={this.getBenefitString(this.getProgramRecommendation(programMetadata.name, candidate))
                                                      + ' - ' + t('SC_FBA_Calc_info_fbaRecommendation')}/>
                            </KatSelectionListItem>
                        ))
                    }
                </KatSelectionList>
            )
        }
    }

    getCardContent(selectedProgram: string) {
        const {t} = this.props;
        if (selectedProgram === SELECT_YOUR_PROGRAM) {
            return (
                <div className="empty-compare">
                    {this.getSelectionListItem()}
                </div>
            );
        }
        if (!this.isProgramFeesDataValid(selectedProgram)) {
            return (<ErrorStatusComponent description={t('SC_FBA_Calc_calculateFeeFailedWarning')}/>)
        }
        const programMetadata = this.getProgramMetadata(selectedProgram);
        const candidate = this.getProgramCandidateForRecommendation();
        const programRecommendation = this.getProgramRecommendation(selectedProgram, candidate);
        const showMiddleBox = isLoginMode() && this.state.showProgramRecommendation && programRecommendation;
        var cardContent;
        if (this.isLoading(selectedProgram)) {
            cardContent = (<LoadingStatusComponent/>);
        } else {
            const programFeeResultWithStatus: ProgramFeeResultWithStatus = this.props.programFeesRes.programFeesStore.programFeeResultWithStatusMap[selectedProgram];
            const programFeeResult = programFeeResultWithStatus.programFeeResult;
            cardContent = (
                <div className="program-card-box-under">
                    <div className={showMiddleBox ? "program-card-box-top-less" : "program-card-box-top"}>
                        <ProgramRevenueComponent
                            selectedProgram={selectedProgram}
                            isMFN={selectedProgram === PROGRAM_NAME_OF_MFN}
                            countryCode={this.props.queryProductRes.countryCode}
                            queryProduct={this.props.queryProductRes.queryProduct}
                            defaultSalesPrice={this.state.salesPrice}
                            defaultShippingCharge={this.state.shippingCharge}
                            recommendationInfo={this.getPriceRecommendation()}
                            handleChangeSalesPrice={this.handleChangeSalesPrice}
                            handleChangeShippingCharge={this.handleChangeShippingCharge}
                            metricsUtil={this.metricsUtil}
                            nextRef={this.costComponentFirstInputRef}
                        />
                        <div id="horizontal-divider-line" className="horizontal-line"></div>
                        <ProgramCostComponent
                            isMFN={selectedProgram === PROGRAM_NAME_OF_MFN}
                            productPrice={this.state.salesPrice}
                            countryCode={this.props.queryProductRes.countryCode}
                            programFeeResult={programFeeResult}
                            handleChangeCostPerUnit={this.handleChangeCostPerUnit}
                            metricsUtil={this.metricsUtil}
                            firstRef={this.costComponentFirstInputRef}
                            nextRef={this.profitComponentFirstInputRef}
                        />
                    </div>
                    {
                        showMiddleBox && programRecommendation ? (
                            <div className="program-card-box-middle">
                                <ProgramRecommendationComponent countryCode={this.props.queryProductRes.countryCode}
                                                                recommendationInfo={programRecommendation}
                                                                handleRecommendationDismiss={this.handleDismissProgramRecommendation}
                                                                metricsUtil={this.metricsUtil}/>
                            </div>
                        ) : <></>
                    }
                    <div className={showMiddleBox ? "program-card-box-bottom-less" : "program-card-box-bottom"}>
                        <ProgramProfitComponent
                            selectedProgram={selectedProgram}
                            isMFN={selectedProgram === PROGRAM_NAME_OF_MFN}
                            salesPrice={this.state.salesPrice}
                            shippingCharge={this.state.shippingCharge}
                            costPerUnit={this.state.costPerUnit}
                            countryCode={this.props.queryProductRes.countryCode}
                            queryProduct={this.props.queryProductRes.queryProduct}
                            programMetadata={programMetadata}
                            recommendationInfo={programRecommendation}
                            metricsUtil={this.metricsUtil}
                            firstRef={this.profitComponentFirstInputRef}
                        />
                    </div>
                    {this.isUpdating(selectedProgram) ?
                        <div className="program-card-box-under-loading">
                            <LoadingStatusComponent/>
                        </div> : <></>
                    }
                </div>
            );
        }
        return cardContent;
    }

    getCloseProgram(selectedProgram: string) {
        if (selectedProgram === SELECT_YOUR_PROGRAM) {
            return (<></>);
        }
        return (
            <div className="program-card-header-icon">
                <KatIcon name="close" size="small" onClick={() => {
                    this.setState({selectedProgram: SELECT_YOUR_PROGRAM});
                }}/>
            </div>
        );
    }

    getRecommendationMark(selectedProgram: string) {
        if (selectedProgram === SELECT_YOUR_PROGRAM || !this.isRecommendedProgram(selectedProgram)) {
            return (<></>);
        }
        const {t} = this.props;
        return (
            <div className="program-card-header-recommended">
                <KatBadge label={t('SC_FBA_Calc_badge_recommended')} type="info" variant="angled"/>
            </div>
        );
    }

    render() {
        const {t} = this.props;
        const selectedProgram = this.state.selectedProgram;

        if (!this.isProgramMetadataValid()) {
            return (<ErrorStatusComponent description={t('SC_FBA_Calc_calculateFeeFailedWarning')}/>)
        }

        return (
            <>
                <KatBox id="ProgramCard" variant="white" className="program-card-box">
                    <>
                        <div className="program-card-header">
                            <div className="program-card-header-name">
                                <KatLabel className="program-card-header-label" text={this.getProgramName(selectedProgram)}/>
                            </div>
                            {this.getRecommendationMark(selectedProgram)}
                            {this.getCloseProgram(selectedProgram)}
                        </div>
                    </>
                    <>
                        {this.getCardContent(selectedProgram)}
                    </>
                </KatBox>
            </>
        );
    }
}

export default withTranslation()(connector(ProgramComponent));
